import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faUsers,
  faEnvelope,
  faUser,
  faBell,
  faFileInvoiceDollar,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faCog,
  faClipboardList,
  faNewspaper,
  faBookOpen,
  faLightbulb,
} from '@fortawesome/free-solid-svg-icons';
import { subscribeToUserChats, getTotalUnreadCount } from '../Services/messagingService';
import { getApi } from '../Services/apiService';
import {
  isAdminWithoutActiveSubscription,
  isUserMemberWithoutLineSubscription,
  isSubscriptionSettingsLocation,
  OPEN_ADMIN_SUBSCRIPTION_MODAL_EVENT,
  OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT,
} from '../Utils/subscriptionAccess';
import { getFamilyTreeIdFromStorage } from '../Utils/headOfLineRole';
import FeatureNoticeModal, {
  FORMS_NOTICE,
  FAMILY_NEWS_NOTICE,
  FAMILY_HISTORY_NOTICE,
  FUN_FACTS_NOTICE,
  FAMILY_TREE_MOBILE_NOTICE,
  blockFamilyTreeNavigationOnMobile,
} from '../Components/Shared/FeatureNoticeModal';

/**
 * Not tabs: these sit at the bottom of the sidebar under an "Upcoming features:"
 * heading instead. Forms, Family News, Family History and FUN FACTS are
 * listed there on every viewport; Family Tree only on phones, where it also drops out of the nav list (CSS hides
 * .nav-item--upcoming-on-mobile). Order matches the approved mock.
 */
const UPCOMING_FEATURES = [
  {
    key: 'family-tree',
    label: 'Family Tree (Vertical)',
    icon: faUsers,
    notice: FAMILY_TREE_MOBILE_NOTICE,
    mobileOnly: true,
  },
  { key: 'forms', label: 'Forms', icon: faClipboardList, notice: FORMS_NOTICE },
  { key: 'family-news', label: 'Family News', icon: faNewspaper, notice: FAMILY_NEWS_NOTICE },
  { key: 'family-history', label: 'Family History', icon: faBookOpen, notice: FAMILY_HISTORY_NOTICE },
  { key: 'fun-facts', label: 'FUN FACTS', icon: faLightbulb, notice: FUN_FACTS_NOTICE },
];

function sidebarSubscriptionGateActive() {
  return (
    isAdminWithoutActiveSubscription() || isUserMemberWithoutLineSubscription()
  );
}

function SidebarDivider() {
  return (
    <li className="sidebar-nav-divider" role="presentation" aria-hidden="true">
      <span className="sidebar-nav-divider-line" />
    </li>
  );
}

function Sidebar({ isCollapsed, onToggle, onNavigate }) {
  const location = useLocation();
  const [subscriptionLocked, setSubscriptionLocked] = useState(() =>
    sidebarSubscriptionGateActive()
  );
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [activeNotice, setActiveNotice] = useState(null);

  const closeNotice = useCallback(() => setActiveNotice(null), []);

  useEffect(() => {
    const bump = () => setSubscriptionLocked(sidebarSubscriptionGateActive());
    window.addEventListener('loggedInUserRoleUpdated', bump);
    window.addEventListener('localStorageChange', bump);
    window.addEventListener('focus', bump);
    return () => {
      window.removeEventListener('loggedInUserRoleUpdated', bump);
      window.removeEventListener('localStorageChange', bump);
      window.removeEventListener('focus', bump);
    };
  }, []);

  /** Block all sidebar navigation until subscription is active; open the correct modal (admin vs member line fee). */
  const NavRow = ({
    path,
    title,
    children,
    testActive,
    linkClassName,
    linkTitle,
    navItemClassName,
    afterLink,
    suppressLiTitle,
    linkAriaDescribedBy,
    disabled,
    onBeforeNavigate,
  }) => {
    const active = disabled
      ? false
      : testActive
        ? testActive()
        : location.pathname.startsWith(path);
    const locked = subscriptionLocked && !disabled;
    const linkCls = [
      'nav-link',
      locked ? 'nav-link--subscription-locked' : '',
      disabled ? 'nav-link--disabled-upcoming' : '',
      linkClassName || '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <li
        className={`nav-item ${active ? 'active' : ''} ${navItemClassName || ''}`.trim()}
        title={suppressLiTitle ? undefined : title}
      >
        {disabled ? (
          <div
            className={linkCls}
            title={linkTitle}
            aria-describedby={linkAriaDescribedBy}
            aria-disabled="true"
            role="presentation"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onBeforeNavigate) onBeforeNavigate();
            }}
          >
            {children}
          </div>
        ) : (
          <Link
            to={path}
            className={linkCls}
            title={linkTitle}
            aria-describedby={linkAriaDescribedBy}
            onClick={(e) => {
              if (locked) {
                e.preventDefault();
                e.stopPropagation();
                if (isSubscriptionSettingsLocation(location)) return;
                if (isAdminWithoutActiveSubscription()) {
                  window.dispatchEvent(new Event(OPEN_ADMIN_SUBSCRIPTION_MODAL_EVENT));
                } else {
                  window.dispatchEvent(new Event(OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT));
                }
                return;
              }
              // Returning false keeps the user on the current page (e.g. mobile family tree notice)
              if (onBeforeNavigate && onBeforeNavigate() === false) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              // The tap really navigates: let the layout close the mobile drawer
              if (onNavigate) onNavigate();
            }}
          >
            {children}
          </Link>
        )}
        {afterLink}
      </li>
    );
  };

  useEffect(() => {
    const localUser = JSON.parse(localStorage.getItem('userDetail') || '{}');
    const userId = localUser?.user_id || localUser?.id || null;
    const familyTreeId = getFamilyTreeIdFromStorage();
    if (!userId || !familyTreeId) return;

    let chatIdsRef = [];
    const fetchUnread = (chats) => {
      chatIdsRef = (chats || []).map((c) => c.id).filter(Boolean);
      getTotalUnreadCount(userId, chatIdsRef).then(setUnreadMessageCount);
    };

    const unsubscribe = subscribeToUserChats(userId, familyTreeId, fetchUnread);

    const onFocus = () => {
      if (chatIdsRef.length > 0) getTotalUnreadCount(userId, chatIdsRef).then(setUnreadMessageCount);
    };
    window.addEventListener('focus', onFocus);

    return () => {
      if (unsubscribe) unsubscribe();
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchUnreadNotificationCount = async () => {
      try {
        const params = new URLSearchParams({
          filter: 'unread',
          limit: '1000',
          offset: '0',
        });
        const response = await getApi(`/notifications?${params.toString()}`);
        const totalFromApi = response?.total;

        if (totalFromApi !== undefined && totalFromApi !== null) {
          const count = Number(totalFromApi);
          if (isMounted) setUnreadNotificationCount(Number.isNaN(count) ? 0 : count);
          return;
        }

        const notificationsData = Array.isArray(response?.data)
          ? response.data
          : response?.data?.data;
        if (isMounted) {
          setUnreadNotificationCount(Array.isArray(notificationsData) ? notificationsData.length : 0);
        }
      } catch (error) {
        if (isMounted) setUnreadNotificationCount(0);
      }
    };

    fetchUnreadNotificationCount();
    const onFocus = () => fetchUnreadNotificationCount();
    const intervalId = setInterval(fetchUnreadNotificationCount, 10000);
    window.addEventListener('focus', onFocus);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // const isActive = (path) => location.pathname.startsWith(path);
  const isActive = (path) => {
    // Dashboard should be active on both '/' and '/dashboard'
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/user/dashboard');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <div className={`sidebar-container ${isCollapsed ? 'collapsed' : ''}`}>
        <Link
          to="/"
          className="sidebar-logo"
          title="Fam Connect"
        >
          <img src={window.getPublicUrl("/assets/images/logo.png")} alt="Fam Connect" />
        </Link>

        <nav className="sidebar-nav">
          <ul className="nav-list">
            <NavRow path="/dashboard" title="Dashboard" testActive={() => isActive('/dashboard')}>
              <div className="nav-icon">
                <FontAwesomeIcon icon={faTachometerAlt} />
              </div>
              {!isCollapsed && <span className="nav-text">Dashboard</span>}
            </NavRow>

            <NavRow
              path="/family-tree"
              title="Family Tree"
              navItemClassName="nav-item--upcoming-on-mobile"
              onBeforeNavigate={() => !blockFamilyTreeNavigationOnMobile()}
            >
              <div className="nav-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              {!isCollapsed && <span className="nav-text">Family Tree</span>}
            </NavRow>

            <NavRow path="/events" title="Planned Events">
              <div className="nav-icon nav-icon-events">
                <img src={window.getPublicUrl("/assets/images/events.svg")} alt="Planned Events" className="nav-icon-events-img" />
              </div>
              {!isCollapsed && <span className="nav-text">Planned Events</span>}
            </NavRow>

            <NavRow
              path="/messages"
              title="Text Messaging"
              navItemClassName={
                unreadMessageCount > 0 ? 'nav-item--messages-tooltip-host' : ''
              }
              suppressLiTitle={unreadMessageCount > 0}
              linkAriaDescribedBy={
                unreadMessageCount > 0 ? 'sidebar-messages-unread-hint' : undefined
              }
              afterLink={
                unreadMessageCount > 0 ? (
                  <div
                    className="sidebar-messages-tooltip"
                    id="sidebar-messages-unread-hint"
                    role="tooltip"
                  >
                    Respond in chats to remove this count
                  </div>
                ) : null
              }
            >
              <div className="nav-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              {!isCollapsed && (
                <span className="nav-text">
                  Text Messaging{unreadMessageCount > 0 ? ` (${unreadMessageCount})` : ''}
                </span>
              )}
            </NavRow>

            <SidebarDivider />

            <NavRow
              path="/settings/profile"
              title="Profile"
              testActive={() => location.pathname.startsWith('/settings/profile')}
            >
              <div className="nav-icon">
                <FontAwesomeIcon icon={faUser} />
              </div>
              {!isCollapsed && <span className="nav-text">Profile</span>}
            </NavRow>

            <NavRow
              path="/settings/subscription"
              title="Subscription"
              testActive={() => location.pathname.startsWith('/settings/subscription')}
            >
              <div className="nav-icon">
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
              </div>
              {!isCollapsed && <span className="nav-text">Subscription</span>}
            </NavRow>

            <NavRow
              path="/settings/notifications"
              title="Notifications"
              testActive={() => location.pathname.startsWith('/settings/notifications')}
            >
              <div className="nav-icon">
                <FontAwesomeIcon icon={faBell} />
              </div>
              {!isCollapsed && (
                <span className="nav-text">
                  Notifications{unreadNotificationCount > 0 ? ` (${unreadNotificationCount})` : ''}
                </span>
              )}
            </NavRow>

            {/* FAQ moved to public landing page; keep for possible restore later
            <NavRow
              path="/faq"
              title="FAQ"
              testActive={() => location.pathname.startsWith('/faq')}
            >
              <div className="nav-icon">
                <FontAwesomeIcon icon={faCircleQuestion} />
              </div>
              {!isCollapsed && <span className="nav-text">FAQ</span>}
            </NavRow>
            */}

            <SidebarDivider />

            <NavRow
              path="/settings/password"
              title="Settings"
              testActive={() =>
                location.pathname === '/settings' ||
                location.pathname.startsWith('/settings/password')
              }
            >
              <div className="nav-icon">
                <FontAwesomeIcon icon={faCog} />
              </div>
              {!isCollapsed && <span className="nav-text">Settings</span>}
            </NavRow>
          </ul>

          <div className="sidebar-upcoming">
            <span
              className="sidebar-nav-divider-line sidebar-upcoming-line"
              aria-hidden="true"
            />
            {!isCollapsed && (
              <p className="sidebar-upcoming-title">Upcoming features:</p>
            )}
            <ul className="sidebar-upcoming-list">
              {UPCOMING_FEATURES.map((feature) => (
                <li
                  key={feature.key}
                  className={feature.mobileOnly ? 'sidebar-upcoming-item--mobile-only' : ''}
                >
                  <button
                    type="button"
                    className="nav-link nav-link--disabled-upcoming sidebar-upcoming-item"
                    title={feature.label}
                    onClick={() => setActiveNotice(feature.notice)}
                  >
                    <span className="nav-icon">
                      <FontAwesomeIcon icon={feature.icon} />
                    </span>
                    {!isCollapsed && <span className="nav-text">{feature.label}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {!isCollapsed && (
          <div className="sidebar-copyright">
            <p className="sidebar-copyright-text">
              © {new Date().getFullYear()} FamConnect26. All rights reserved.
            </p>
          </div>
        )}
{/* 
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={openLogoutModal}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div> */}

        <button className="collapse-toggle" onClick={onToggle}>
          <FontAwesomeIcon icon={isCollapsed ? faAngleDoubleRight : faAngleDoubleLeft} />
        </button>
      </div>

      {/* <LogoutModal isOpen={isLogoutModalOpen} onClose={closeLogoutModal} /> */}

      {activeNotice && (
        <FeatureNoticeModal
          title={activeNotice.title}
          message={activeNotice.message}
          onClose={closeNotice}
        />
      )}
    </>
  );
}

export default Sidebar;
